import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  PlusCircle, Edit2, Trash2, Save, X, Loader2,
  ChevronUp, ChevronDown, GripVertical, Image, Users2,
  AlignLeft, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

/* ── Marquee Preview ───────────────────────────────────────────────── */
function MarqueePreview({ providers }) {
  const active = providers.filter(p => p.is_active);
  if (active.length === 0) return null;

  // Duplicate for seamless loop
  const doubled = [...active, ...active];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 overflow-hidden">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">Marquee Preview</h2>
      <div className="relative overflow-hidden">
        <style>{`
          @keyframes marquee-scroll {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          .marquee-track {
            display: flex;
            gap: 2rem;
            animation: marquee-scroll 20s linear infinite;
            width: max-content;
          }
        `}</style>
        <div className="marquee-track">
          {doubled.map((p, i) => (
            <div key={`${p.id}-${i}`}
              className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl shrink-0">
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="h-5 w-auto object-contain" />
              ) : (
                <span className="text-sm font-semibold text-slate-600">{p.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Single Provider Modal ─────────────────────────────────────────── */
function ProviderModal({ provider, onClose, onSaved }) {
  const isEdit = Boolean(provider?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: provider?.name || '',
    logo_url: provider?.logo_url || '',
    website_url: provider?.website_url || '',
    sort_order: provider?.sort_order ?? 0,
    is_active: provider?.is_active !== false,
  });

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Provider name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order) || 0,
      };
      const url = isEdit
        ? `${API}/api/marketing/social-proof/${provider.id}`
        : `${API}/api/marketing/social-proof`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Provider updated' : 'Provider created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">
            {isEdit ? 'Edit Provider' : 'Add Provider'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Provider Name *</label>
            <input type="text" value={form.name} onChange={set('name')} required
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              placeholder="e.g. NDIS Commission" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Logo URL (optional)</label>
            <input type="url" value={form.logo_url} onChange={set('logo_url')}
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              placeholder="https://example.com/logo.png" />
            {form.logo_url && (
              <div className="mt-2 p-2 bg-slate-50 rounded-lg border border-slate-200 inline-flex">
                <img src={form.logo_url} alt="Logo preview" className="h-8 w-auto object-contain"
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Website URL (optional)</label>
            <input type="url" value={form.website_url} onChange={set('website_url')}
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              placeholder="https://example.com" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" min={0} value={form.sort_order} onChange={set('sort_order')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input type="checkbox" id="sp_active" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-primary-600 rounded" />
              <label htmlFor="sp_active" className="text-sm font-medium text-slate-700">Active</label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Bulk Add Modal ────────────────────────────────────────────────── */
function BulkAddModal({ onClose, onSaved }) {
  const token = localStorage.getItem('token');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleBulkAdd() {
    const names = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (names.length === 0) { toast.error('Enter at least one provider name'); return; }
    setSaving(true);
    let created = 0;
    let failed = 0;
    try {
      for (const name of names) {
        try {
          const res = await fetch(`${API}/api/marketing/social-proof`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name, is_active: true, sort_order: 0 }),
          });
          if (res.ok) created++;
          else failed++;
        } catch {
          failed++;
        }
      }
      if (created > 0) toast.success(`Created ${created} provider${created !== 1 ? 's' : ''}`);
      if (failed > 0) toast.error(`${failed} failed to create`);
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const preview = text.split('\n').map(l => l.trim()).filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Bulk Add Providers</h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Provider Names (one per line)
            </label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={8}
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none font-mono"
              placeholder={"NDIS Commission\nAged Care Quality\nMedicare\nDVA"} />
          </div>
          {preview.length > 0 && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 mb-2">{preview.length} provider{preview.length !== 1 ? 's' : ''} to add:</p>
              <div className="flex flex-wrap gap-1.5">
                {preview.map((name, i) => (
                  <span key={i} className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-full">{name}</span>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button onClick={handleBulkAdd} disabled={saving || preview.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
              {saving ? 'Adding...' : `Add ${preview.length > 0 ? preview.length : ''} Provider${preview.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────────── */
export default function SocialProofEditor() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);    // null | 'new' | provider object
  const [bulkModal, setBulkModal] = useState(false);
  const token = localStorage.getItem('token');

  async function fetchProviders() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/social-proof/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchProviders(); }, []);

  async function deleteProvider(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/marketing/social-proof/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Provider deleted');
      fetchProviders();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function toggleActive(p) {
    try {
      await fetch(`${API}/api/marketing/social-proof/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...p, is_active: !p.is_active }),
      });
      fetchProviders();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function reorder(index, direction) {
    const arr = [...providers];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= arr.length) return;
    [arr[index], arr[swap]] = [arr[swap], arr[index]];
    setProviders(arr);
    try {
      await Promise.all(
        arr.map((p, i) =>
          fetch(`${API}/api/marketing/social-proof/${p.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...p, sort_order: i }),
          })
        )
      );
      fetchProviders();
    } catch {
      toast.error('Failed to reorder');
    }
  }

  return (
    <div className="p-6 space-y-6">
      {modal && (
        <ProviderModal
          provider={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchProviders}
        />
      )}
      {bulkModal && (
        <BulkAddModal
          onClose={() => setBulkModal(false)}
          onSaved={fetchProviders}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Social Proof</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {providers.length} provider{providers.length !== 1 ? 's' : ''} — shown in the marquee on the homepage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setBulkModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors">
            <AlignLeft size={16} />
            Bulk Add
          </button>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            <PlusCircle size={16} />
            Add Provider
          </button>
        </div>
      </div>

      {/* Marquee preview */}
      {!loading && <MarqueePreview providers={providers} />}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : providers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Users2 size={40} className="mb-3 opacity-30" />
            <p className="font-medium">No providers yet</p>
            <p className="text-sm mt-1">Add providers or use Bulk Add to get started quickly</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 w-8"></th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Provider</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Logo</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {providers.map((p, index) => (
                <tr key={p.id} className={clsx('hover:bg-slate-50 transition-colors', !p.is_active && 'opacity-50')}>
                  <td className="px-5 py-3.5">
                    <GripVertical size={14} className="text-slate-300" />
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                    {p.website_url && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[180px]">
                        {p.website_url.replace(/^https?:\/\//, '')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    {p.logo_url ? (
                      <div className="flex items-center gap-2">
                        <img src={p.logo_url} alt={p.name} className="h-6 w-auto object-contain"
                          onError={e => { e.target.style.display = 'none'; }} />
                        <span className="text-xs text-slate-400 truncate max-w-[100px]">{p.logo_url.split('/').pop()}</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        <Image size={10} /> Text only
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <button onClick={() => toggleActive(p)}
                      className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors',
                        p.is_active
                          ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      )}>
                      {p.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => reorder(index, 'up')} disabled={index === 0}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronUp size={14} />
                      </button>
                      <button onClick={() => reorder(index, 'down')} disabled={index === providers.length - 1}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronDown size={14} />
                      </button>
                      <button onClick={() => setModal(p)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteProvider(p.id, p.name)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
